import { Search } from "lucide-react";
import { FormInput, Heading, Loader, FormSelect, Dialog } from "@/components";
import {
  Button,
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { COMMUNITY_NAME, DOCUMENT_TYPE, featureItems } from "./consts";
import type { TabValue } from "./types";
import useGLConfigurationStudio from "./useGLConfigurationStudio";

const GLConfigurationStudio = () => {
  const {
    activeTab,
    applicableOnly,
    breadcrumbData,
    rows,
    tabs,
    fieldSearch,
    onRowChange,
    requiredFields,
    form,
    onSubmit,
    setActiveTab,
    setApplicableOnly,
    setFieldSearch,
    documentTypes,
    isDocumentTypesLoading,
    summaryCards,
    summaryCardList,
    isConfigLoading,
    isConfigRefetching,
    isUpdatePending,
    openConfirmationDialog,
    setOpenConfirmationDialog,
    onConfirmUpdate,
    selectedRowData,
    hasUpdatePermission
  } = useGLConfigurationStudio();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-start lg:gap-6">
        <div className="lg:col-span-6">
          <Heading
            title="GL Configuration Studio"
            breadcrumbData={breadcrumbData}
          />
        </div>

        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-[12px] leading-5 text-slate-600 lg:col-span-6">
          Configure header and line-item fields by community and document type.
          This setup will drive manual entry, Excel template generation, and
          upload validation.
        </div>
      </div>

      {isDocumentTypesLoading ? (
        <Loader />
      ) : (
        <>
          <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
            <div className="rounded-xl bg-white p-4 shadow-md sm:p-5 lg:col-span-7">
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex min-h-[126px] flex-col"
              >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormInput
                    name={COMMUNITY_NAME}
                    control={form.control}
                    label="Community"
                    placeholder="Please enter"
                    disabled
                  />

                  <FormSelect
                    name={DOCUMENT_TYPE}
                    control={form.control}
                    label="Document Type"
                    placeholder="Please select"
                    isRequired={requiredFields.includes(DOCUMENT_TYPE)}
                    error={errors?.[DOCUMENT_TYPE]?.message as string}
                    options={documentTypes?.map((data: any) => ({
                      label: `${data?.documentType} - ${data?.documentTypeDisplayName}`,
                      value: data?.documentTypeId?.toString()})
                    )}
                  />
                </div>

                <div className="mt-10 flex justify-end">
                  <Button type="submit">Submit</Button>
                </div>
              </form>
            </div>

            <div className="rounded-xl bg-white p-4 shadow-md sm:p-5 lg:col-span-5">
              <div className="space-y-4 pt-1">
                {featureItems.map(({ title, icon: Icon }) => (
                  <div
                    key={title}
                    className="flex items-center gap-3 text-sm text-slate-600"
                  >
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span>{title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {isConfigLoading ? <Loader /> : summaryCards ? (
            <>
              <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-4">
                {summaryCardList?.map((card: any) => {
                  const Icon = card.icon;

                  return (
                    <div
                      key={card.title}
                      className="rounded-xl border-b-[3px] border-primary bg-white p-4 shadow-md"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-15 w-15 items-center justify-center rounded-md bg-slate-50">
                          <Icon className="h-6 w-6 text-primary" />
                        </div>

                        <div className="text-right">
                          <div className="text-[11px] text-slate-500">
                            {card.title}
                          </div>
                          <div className="text-2xl font-[600] text-primary">
                            {card.value}
                          </div>
                          <div className="mt-2 text-[12px] text-slate-500">
                            {card.subtitle}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 rounded-xl bg-white p-4 shadow-md sm:p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="text-md font-semibold text-slate-900">
                      Field Configuration Matrix
                    </div>
                    <div className="mt-1 max-w-2xl text-sm text-slate-500">
                      Maintain applicability, requiredness, editability, default
                      value, and source type by posting context.
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 sm:flex-row">
                    <div className="w-full sm:w-[220px]">
                      <Input
                        value={fieldSearch}
                        onChange={(event) => setFieldSearch(event.target.value)}
                        placeholder="Search field name"
                        startIcon={<Search className="h-4 w-4" />}
                      />
                    </div>

                    <div className="flex h-[40px] w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-1 text-base text-muted-foreground shadow-xs md:text-sm sm:w-[220px]">
                      <span className="whitespace-nowrap">Applicable only</span>
                      <Switch
                        checked={applicableOnly}
                        onCheckedChange={setApplicableOnly}
                        disabled={isConfigRefetching}
                      />
                    </div>
                  </div>
                </div>
                <Tabs
                  value={activeTab}
                  onValueChange={(value) => setActiveTab(value as TabValue)}
                  className="mt-6"
                >
                  <TabsList className="h-auto rounded-full bg-slate-100 p-1">
                    {tabs.map((tab) => (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="rounded-full px-5 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                      >
                        {tab.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {tabs.map((tab) => (
                    <TabsContent key={tab.value} value={tab.value} className="mt-6">
                      <div className="mb-5">
                        <div className="text-base font-semibold text-slate-900">
                          {tab.label === "Header Fields"
                            ? "Header-Level Configuration"
                            : "Line-Item-Level Configuration"}
                        </div>
                        <div className="mt-1 text-sm text-slate-500">
                          {tab.description}
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-xl border border-slate-200">
                        <Table className="min-w-[1100px]">
                          <TableHeader className="w-full table-fixed border-collapse">
                            <TableRow className="table w-full table-fixed hover:bg-transparent">
                              <TableHead className="h-12 px-4 text-xs font-semibold text-primary">
                                Field Name
                              </TableHead>
                              <TableHead className="h-12 px-4 text-xs font-semibold text-primary">
                                Applicable
                              </TableHead>
                              <TableHead className="h-12 px-4 text-xs font-semibold text-primary">
                                Required
                              </TableHead>
                              <TableHead className="h-12 px-4 text-xs font-semibold text-primary">
                                Default Value
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody className="block max-h-[calc(100vh-420px)] w-full overflow-y-scroll">
                            {rows?.map((row: any) => (
                              <TableRow
                                key={`${tab.value}-${row.rowIndex}`}
                                className="table w-full table-fixed"
                              >
                                <TableCell className="px-4 py-4 align-top">
                                  <div className="font-medium text-slate-700">
                                    {row?.displayLabel || ""}
                                  </div>
                                  <div className="mt-1 text-xs text-slate-400 max-w-[250px] text-wrap">
                                    {row?.helpText || ""}
                                  </div>
                                </TableCell>
                                <TableCell className="px-4 py-4">
                                  <Switch
                                    checked={row.applicable}
                                    disabled={row?.applicableLocked || isUpdatePending || isConfigRefetching || !hasUpdatePermission}
                                    onCheckedChange={(checked) =>
                                      onRowChange(
                                        row?.ujeFieldMasterId,
                                        "applicable",
                                        !!checked
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="px-4 py-4">
                                  <Switch
                                    disabled={row?.requiredLocked || isUpdatePending || !hasUpdatePermission}
                                    checked={row.required || isConfigRefetching}
                                    onCheckedChange={(checked) =>
                                      onRowChange(
                                        row?.ujeFieldMasterId,
                                        "required",
                                        !!checked
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell className="px-4 py-4">
                                  <Input
                                    value={row.defaultValue}
                                    disabled={isUpdatePending || isConfigRefetching || !hasUpdatePermission}
                                    placeholder="Enter Default"
                                  />
                                </TableCell>

                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </>
          ) : null}
        </>
      )}
      <Dialog
        open={openConfirmationDialog}
        setOpen={setOpenConfirmationDialog}
        title={<div className="text-2xl font-bold text-primary">
          {"Confirmation"}
          <div className="h-1 w-[30%] bg-primary rounded-full" />
        </div>}
        onSave={onConfirmUpdate}
      >
        <p>Are you sure you want to update status of <span className="text-primary font-semibold">{selectedRowData?.label || ""}</span> </p>
      </Dialog>
    </div>
  );
};

export default GLConfigurationStudio;